import { Injectable, OnDestroy } from "@angular/core";
import { Observable, BehaviorSubject, of } from "rxjs";

import { GRAPHQL_OPERATION_NAMES, HttpService } from "./http.service";
import { SubSink } from "subsink";
import { GAService } from "./ga.service";
import { NavigationService } from "./navigation.service";
import { switchMap } from "rxjs/operators";
import { E_MhAndPrFormMode } from "../enums/mh-and-pr-form-mode.enum";
import { toQuotedString } from "../utils/string";
import { MedicalHistoryQuestionsEntry } from "@apps/patient-core/src/app/data_model/medical-history";
import { toUnquotedJSON } from "@shared/json-utils";
import { AnalyticsService, METRIC } from "./analytics.service";
import { BaseAuthService } from "./abstracts/auth.service";
import { BasePatientsService } from "@apps/patient-core/src/app/services/abstracts/patients.service";

declare let window: Window;

@Injectable({
  providedIn: "root",
})
export class MedicalHistoryService implements OnDestroy {
  onMedicalHistoryGetQuestionsChanged: BehaviorSubject<{
    status: string | null;
    medical_history?: MedicalHistoryQuestionsEntry | null;
    msg?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error?: any;
  }> = new BehaviorSubject({
    status: null,
    medical_history: null,
    msg: "",
    error: "",
  });
  onModeChanged: BehaviorSubject<E_MhAndPrFormMode> = new BehaviorSubject(E_MhAndPrFormMode.FORM);

  public medicalHistoryQuestions: MedicalHistoryQuestionsEntry;
  private _subs = new SubSink();
  private _mode: E_MhAndPrFormMode = E_MhAndPrFormMode.FORM;
  constructor(
    private _httpService: HttpService,
    private _gAService: GAService,
    private _navigationService: NavigationService,
    public _authService: BaseAuthService,
    private _analyticsService: AnalyticsService,
    private _patientsService: BasePatientsService
  ) {}

  ngOnDestroy() {
    this._subs.unsubscribe();
  }

  hasAnsweredYes(): Observable<boolean> {
    return this._httpService
      .query(
        GRAPHQL_OPERATION_NAMES.MEDICAL_HISTORY_HAS_YES_ANSWERS,
        `{
        patient {
          medical_history {
            has_yes_answers
          }
        }
      }`
      )
      .pipe(
        switchMap((response) => {
          if (!response.data.patient.medical_history) return of(false);

          return of(response.data.patient.medical_history.has_yes_answers);
        })
      );
  }

  getMedicalHistoryQuestions(): void {
    this.onMedicalHistoryGetQuestionsChanged.next({ status: "LOADING" });
    this._subs.sink = this._httpService
      .query(
        GRAPHQL_OPERATION_NAMES.MEDICAL_HISTORY_QUESTIONS,
        `{
      patient {
        date_of_birth
        medical_history_questions {
          questions {
            details
            follow_up_question
            position
            question_id
            text
            value
            can_change
            tooltips
          }
          patient_notes
        }
      }
    }`
      )
      .subscribe(
        (response) => {
          if (response === "NOT_LOGGEDIN") {
            window.location.href = window.location.pathname.substring(1).split("/")[0];
          }
          this._gAService.action("medicalhistory_questions_loaded_success");
          const medical_history_questions = response?.data?.patient?.medical_history_questions;

          let medical_history: MedicalHistoryQuestionsEntry | null = null;
          if (medical_history_questions) {
            medical_history = new MedicalHistoryQuestionsEntry(medical_history_questions);
            if (!medical_history.patient_notes) medical_history.patient_notes = "";
            medical_history.dateOfBirth = response?.data?.patient?.date_of_birth || this._patientsService.patientInfo?.date_of_birth;
            this.medicalHistoryQuestions = medical_history;
          }

          this.onMedicalHistoryGetQuestionsChanged.next({
            status: "LOADED",
            medical_history,
          });
        },
        (err) => {
          if (err === "NOT_LOGGEDIN") {
            this._gAService.error("medicalhistory_questions_loaded_error_not_logged_in");
            window.location.href = window.location.pathname.substring(1).split("/")[0];
          }
          this._gAService.error("medicalhistory_questions_loaded_error");
        }
      );
  }

  private _trackSigned() {
    const referrer = this._authService.referrerAsJson;
    const { site_id } = this._authService.getJWT();

    this._analyticsService.track(new METRIC.ActionSigned_MedicalHistory(site_id, referrer));
  }

  saveMedicalHistory(): void {
    this._trackSigned();
    const { questions, patient_notes, encoded_signature, signature_used } = this.medicalHistoryQuestions;
    this.onMedicalHistoryGetQuestionsChanged.next({ status: "SAVING" });
    this._gAService.action("medicalhistory_saving");
    const updated_questions = questions.map((question) => question.toBase());
    const query = `{
      createPatientMedicalHistory(new_item: {
        patient_notes: ${toQuotedString(patient_notes)}
        questions: ${toUnquotedJSON(updated_questions)}
        encoded_signature: "${encoded_signature}"
        signature_used: ${signature_used}
      })
    }`;
    this._subs.sink = this._httpService.mutation("createPatientMedicalHistory", query).subscribe(
      (response) => {
        if (response.errors?.length) {
          this._handleCreateError(JSON.parse(response.errors[0].message));
        } else {
          this.onMedicalHistoryGetQuestionsChanged.next({
            status: "UPDATED",
          });
          this.onMedicalHistoryGetQuestionsChanged.next({ status: "SAVED" });
          this._gAService.goal("medicalhistory_saved");
          this.onMedicalHistoryGetQuestionsChanged.next({
            status: "LOADING",
          }); // BUGFIX due to next page load seeing saving state

          this._navigationService.navigate(this._authService.isRestricted ? "/restricted/medical-history/medical-history-completed" : "/my-dental");
        }
      },
      (error) => {
        this._handleCreateError({ error });
      }
    );
  }

  private _handleCreateError(error) {
    let msg = "Error saving your medical history. Please try again.";

    if (error.error === "NOT_LOGGEDIN") {
      msg = "Error saving your medical history. You are not signed in. Refreshing...";
      setTimeout(() => {
        window.location.href = window.location.pathname.substring(1).split("/")[0];
      }, 4000);
    }

    this.onMedicalHistoryGetQuestionsChanged.next({
      status: "ERROR_SAVING",
      msg,
      error,
    });
  }
}
